import { useEffect, useState } from "react";
import { readBalance, type BalanceRpcs } from "../balances";
import type { Chain, Token } from "../chains";

/** The time, ticking every `ms`. */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

const FRESH_MS = 20_000;
const cache = new Map<string, { v: bigint | null; at: number; p?: Promise<bigint | null> }>();
const keyOf = (chain: Chain, token: Token, owner: string) => `${chain.key}:${token.addr}:${owner}`;

function fetchBalance(chain: Chain, token: Token, owner: string, rpcs: BalanceRpcs, force: boolean): Promise<bigint | null> {
  const k = keyOf(chain, token, owner);
  const hit = cache.get(k);
  if (hit && !force && (hit.p || Date.now() - hit.at < FRESH_MS)) return hit.p ?? Promise.resolve(hit.v);
  const p = readBalance(chain, token, owner, rpcs).then((v) => {
    cache.set(k, { v, at: Date.now() });
    return v;
  });
  cache.set(k, { v: hit?.v ?? null, at: hit?.at ?? 0, p });
  return p;
}

/** Balances of `tokens` on `chain` for `owner` (undefined while loading, null when unreadable).
 *  `bump` forces a re-read (after a swap). */
export function useBalances(chain: Chain | null, tokens: Token[], owner: string | undefined, rpcs: BalanceRpcs, bump = 0): Record<string, bigint | null | undefined> {
  const [out, setOut] = useState<Record<string, bigint | null | undefined>>({});
  const sig = tokens.map((t) => t.addr).join(",");
  useEffect(() => {
    if (!chain || !owner) return setOut({});
    let live = true;
    const initial: Record<string, bigint | null | undefined> = {};
    for (const t of tokens) initial[t.addr] = cache.get(keyOf(chain, t, owner))?.v ?? undefined;
    setOut(initial);
    const load = (force: boolean) =>
      tokens.forEach((t) =>
        fetchBalance(chain, t, owner, rpcs, force).then((v) => {
          if (live) setOut((o) => ({ ...o, [t.addr]: v }));
        }),
      );
    load(bump > 0);
    const timer = setInterval(() => !document.hidden && load(true), 30_000);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [chain?.key, sig, owner, rpcs.solanaRpc, rpcs.jupnetRpc, bump]);
  return out;
}

/** A value kept in this browser (a remembered setting), falling back when storage is unavailable. */
export function useStored<T>(key: string, fallback: T): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => {
    try {
      const s = localStorage.getItem(key);
      return s === null ? fallback : (JSON.parse(s) as T);
    } catch {
      return fallback;
    }
  });
  const set = (x: T) => {
    setV(x);
    try {
      localStorage.setItem(key, JSON.stringify(x));
    } catch {
      // a setting not remembered is fine
    }
  };
  return [v, set];
}
